<template>
  <div class="ccma-webshop-settings">
    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-container">
        <Loader type="infinite-spinner" :size="'large'" />
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="plan-selection-container">
      <!-- State Badges - Mutually exclusive (Order: Retry Pending → Not Enrolled → Expired → Trial → Premium) -->
      <div v-if="isRetryPending" class="active-subscription-badge">{{ t('webshopSettings.activeSubscription') }}</div>
      <div v-else-if="isExpiredOrFailed && !isPendingRenewalProcessing" class="no-active-subscription-badge">{{ t('webshopSettings.noActiveSubscription') }}</div>
      <div v-else-if="isExpiredOrFailed && isPendingRenewalProcessing" class="active-subscription-badge">{{ t('webshopSettings.activeSubscription') }}</div>
      <div v-else-if="isInTrial" class="trial-badge">{{ t('webshopSettings.trialPeriod') }}</div>
      <div v-else-if="isPremium" class="active-subscription-badge">{{ t('webshopSettings.activeSubscription') }}</div>

      <!-- Title -->
      <h1 class="main-title">
        {{ isNotEnrolled
          ? t('webshopSettings.start90DaysTrial')
          : isInTrial && !isExpiredOrFailed
            ? t('webshopSettings.trialActiveMessage')
          : isPremium && !isFailed
            ? t('webshopSettings.manageYourPlan')
            : t('webshopSettings.chooseYourPlan')
        }}
      </h1>

      <!-- Not Enrolled State: No alert box (goes directly to message section) -->

      <!-- Retry Pending State: Invalid Payment Method Alert -->
      <div v-if="isRetryPending" class="invalid-payment-alert">
        <h3 class="invalid-payment-title">{{ t('webshopSettings.invalidPaymentMethod') }}</h3>
        <p class="invalid-payment-message">{{ t('webshopSettings.invalidPaymentMethodMessage') }}</p>
        <p v-if="nextBillingDate" class="invalid-payment-date">
          {{ t('webshopSettings.paymentDue') }}: <span class="invalid-payment-date-value">{{ nextBillingDate }}</span>
        </p>
      </div>

      <!-- Expired State: No Current Subscription Alert -->
      <div v-else-if="isExpiredOrFailed" class="no-subscription-alert">
        <h3 class="no-subscription-title">
          {{ isPendingRenewalProcessing
            ? t('webshopSettings.renewalProcessingTitle')
            : t('webshopSettings.noCurrentSubscription')
          }}
        </h3>
        <p class="no-subscription-message">
          {{ isPendingRenewalProcessing
            ? t('webshopSettings.renewalProcessingMessage')
            : t('webshopSettings.noCurrentSubscriptionMessage')
          }}
        </p>
      </div>

      <!-- Trial State: Current Selection Box or No Plan Alert -->
      <div v-else-if="isInTrial && pendingPlanDetails" class="current-selection-box">
        <h3 class="selection-title">{{ t('webshopSettings.currentSelection') }}</h3>
        <div class="selection-plan">
          {{ pendingPlanDetails.name }} {{ t('webshopSettings.plan') }} - {{ formatEuroPrice(pendingPlanDetails.price) }} {{ pendingPlanDetails.frequency }}
        </div>
        <p class="selection-message">{{ t('webshopSettings.trialSelectionMessage') }}</p>
      </div>
      <div v-else-if="isInTrial && !pendingPlanDetails" class="no-plan-alert">
        <template v-if="showTrialPlanSelectionState">
          <strong>{{ t('webshopSettings.noPlanSelected') }}</strong>
          <p>{{ t('webshopSettings.noPlanSelectedMessage') }}</p>
          <p>{{ t('webshopSettings.noPlanSelectedAssuranceMessage') }}</p>
        </template>
        <p v-else>{{ t('webshopSettings.trialPlanSelectionNearEndMessage', { countdown: trialSelectionCountdownText }) }}</p>
      </div>

      <!-- Premium State: Current Plan Info with Pending Change -->
      <div v-else-if="isPremium && hasPendingChange && currentPlanDetails" class="current-plan-info-box">
        <h3 class="current-plan-title">
          {{ t('webshopSettings.currentPlan') }}: {{ currentPlanDetails.name }} - {{ formatEuroPrice(currentPlanDetails.price) }}/{{ currentPlanDetails.frequency }}
        </h3>
        <p class="current-plan-message">{{ t('webshopSettings.planChangeMessage') }}</p>
      </div>

      <!-- Premium State: Current Plan Info WITHOUT Pending Change -->
      <div v-else-if="isPremium && !hasPendingChange && !isCancelled && currentPlanDetails" class="current-plan-info-box-simple">
        <h3 class="current-plan-title-simple">
          {{ t('webshopSettings.currentPlan') }}: {{ currentPlanDetails.name }} - {{ formatEuroPrice(currentPlanDetails.price) }}/{{ currentPlanDetails.frequency }}
        </h3>
        <p class="current-plan-message-simple">{{ t('webshopSettings.upgradeMessage') }}</p>
      </div>

      <!-- Premium State: Cancelled Subscription -->
      <div v-else-if="isPremium && isCancelled && currentPlanDetails" class="cancelled-subscription-box">
        <h3 class="cancelled-subscription-title">
          {{ t('webshopSettings.currentPlan') }}: {{ currentPlanDetails.name }} - {{ formatEuroPrice(currentPlanDetails.price) }}/{{ currentPlanDetails.frequency }}
        </h3>
        <p class="cancelled-subscription-message">{{ t('webshopSettings.subscriptionCancelledMessage') }}</p>
      </div>

      <!-- Expired State: Messages -->
      <div v-if="isExpiredOrFailed && !isRetryPending" class="expired-messages">
        <p v-if="isPendingRenewalProcessing" class="get-started-text">
          {{ t('webshopSettings.renewalProcessingPendingLabel') }}
        </p>
        <template v-else>
          <p class="get-started-text">{{ t('webshopSettings.getStartedToday') }}</p>
          <p class="choose-plan-text">{{ t('webshopSettings.choosePlanToBegin') }}</p>
        </template>
      </div>

      <!-- Trial State: End Date -->
      <p v-else-if="isInTrial && trialEndDate" class="trial-info">
        {{ t('webshopSettings.trialEndsOn') }}
        <span class="trial-date">{{ trialEndDate }}</span>
      </p>

      <!-- Premium State: Pending Change Alert -->
      <div v-else-if="isPremium && hasPendingChange && !isRetryPending" class="pending-change-alert">
        <div class="alert-icon">⚠️</div>
        <div class="alert-content">
          <strong>{{ t('webshopSettings.pendingChange') }}</strong>
          <p>
            {{ t('webshopSettings.pendingChangeMessage', { renewalDate: nextBillingDate || '' }) }}
          </p>
        </div>
      </div>

      <!-- Premium State: Subscription Status -->
      <div v-else-if="isPremium && !isRetryPending" class="subscription-status">
        <p class="status-active">
          {{ isCancelled
            ? t('webshopSettings.subscriptionEndsOn', { renewalDate: nextBillingDate || '' })
            : t('webshopSettings.subscriptionActive')
          }}
        </p>
        <p v-if="nextBillingDate && !isCancelled" class="next-billing">
          {{ t('webshopSettings.nextBilling') }}: <span class="billing-date">{{ nextBillingDate }}</span>
        </p>
      </div>

      <!-- Plan Selection Prompt - Show when user has payment method set up -->
      <p v-if="showPlanSelectionPrompt && !isPendingRenewalProcessing && !isRetryPending && !isFailed && (!isInTrial || showTrialPlanSelectionState)" class="plan-selection-prompt">
        {{ t('webshopSettings.selectAPlan') }}
      </p>

      <!-- Plan Cards -->
      <div v-if="!isInTrial || showTrialPlanSelectionState" class="plans-grid">
        <!-- Monthly Plan -->
        <div 
          v-if="plans.monthly"
          class="plan-card"
          :class="{ 
            'selected': selectedPlan === 'monthly' && currentPlanId !== 'monthly' && pendingPlanId !== 'monthly',
            'is-current': isPremium && currentPlanId === 'monthly',
            'is-pending': pendingPlanId === 'monthly',
            'is-disabled': isNotEnrolled || (isCancelled && !isExpired) || isPendingRenewalProcessing || isTrialWithoutRenewalCard || isRetryPending
          }"
          @click="!isNotEnrolled && (!isCancelled || isExpired) && !isPendingRenewalProcessing && !isTrialWithoutRenewalCard && !isRetryPending && handlePlanClick('monthly')"
        >
          <div v-if="isPremium && currentPlanId === 'monthly'" class="current-plan-badge">{{ t('webshopSettings.current') }}</div>
          <div v-if="pendingPlanId === 'monthly'" class="pending-plan-badge">{{ t('webshopSettings.pending') }}</div>
          <h3 class="plan-name">{{ t('webshopSettings.monthly') }}</h3>
          <div class="plan-price">{{ formatEuroPrice(plans.monthly.price) }}</div>
          <p class="plan-frequency">{{ plans.monthly.frequency }}</p>
        </div>

        <!-- Quarterly Plan -->
        <div 
          v-if="plans.quarterly"
          class="plan-card"
          :class="{ 
            'selected': selectedPlan === 'quarterly' && currentPlanId !== 'quarterly' && pendingPlanId !== 'quarterly',
            'is-current': isPremium && currentPlanId === 'quarterly',
            'is-pending': pendingPlanId === 'quarterly',
            'is-disabled': isNotEnrolled || (isCancelled && !isExpired) || isPendingRenewalProcessing || isTrialWithoutRenewalCard || isRetryPending
          }"
          @click="!isNotEnrolled && (!isCancelled || isExpired) && !isPendingRenewalProcessing && !isTrialWithoutRenewalCard && !isRetryPending && handlePlanClick('quarterly')"
        >
          <div v-if="isPremium && currentPlanId === 'quarterly'" class="current-plan-badge">{{ t('webshopSettings.current') }}</div>
          <div v-if="pendingPlanId === 'quarterly'" class="pending-plan-badge">{{ t('webshopSettings.pending') }}</div>
          <h3 class="plan-name">{{ t('webshopSettings.quarterly') }}</h3>
          <div class="plan-price">{{ formatEuroPrice(plans.quarterly.price) }}</div>
          <p class="plan-frequency">{{ plans.quarterly.frequency }}</p>
        </div>

        <!-- Bi-Annual Plan (legacy — no longer selectable, only shown for existing subscribers) -->
        <div 
          v-if="plans.biannual && (currentPlanId === 'biannual' || pendingPlanId === 'biannual')"
          class="plan-card"
          :class="{ 
            'is-current': isPremium && currentPlanId === 'biannual',
            'is-pending': pendingPlanId === 'biannual',
            'is-disabled': true
          }"
        >
          <div v-if="isPremium && currentPlanId === 'biannual'" class="current-plan-badge">{{ t('webshopSettings.current') }}</div>
          <div v-if="pendingPlanId === 'biannual'" class="pending-plan-badge">{{ t('webshopSettings.pending') }}</div>
          <h3 class="plan-name">{{ t('webshopSettings.biAnnual') }}</h3>
          <div class="plan-price">{{ formatEuroPrice(plans.biannual.price) }}</div>
          <p class="plan-frequency">{{ plans.biannual.frequency }}</p>
        </div>

        <!-- Annual Plan -->
        <div 
          v-if="plans.annual"
          class="plan-card"
          :class="{ 
            'selected': selectedPlan === 'annual' && currentPlanId !== 'annual' && pendingPlanId !== 'annual',
            'is-current': isPremium && currentPlanId === 'annual',
            'is-pending': pendingPlanId === 'annual',
            'is-disabled': isNotEnrolled || (isCancelled && !isExpired) || isPendingRenewalProcessing || isTrialWithoutRenewalCard || isRetryPending
          }"
          @click="!isNotEnrolled && (!isCancelled || isExpired) && !isPendingRenewalProcessing && !isTrialWithoutRenewalCard && !isRetryPending && handlePlanClick('annual')"
        >
          <div v-if="isPremium && currentPlanId === 'annual'" class="current-plan-badge">{{ t('webshopSettings.current') }}</div>
          <div v-if="pendingPlanId === 'annual'" class="pending-plan-badge">{{ t('webshopSettings.pending') }}</div>
          <h3 class="plan-name">{{ t('webshopSettings.annual') }}</h3>
          <div class="plan-price">{{ formatEuroPrice(plans.annual.price) }}</div>
          <p class="plan-frequency">{{ plans.annual.frequency }}</p>
        </div>
      </div>

      <!-- Payment Setup Required - Show in Expired state when NO payment method -->
      <div v-if="isExpiredOrFailed && !renewalCard && !isRetryPending" class="payment-setup-info">
        <h3 class="payment-setup-title">{{ t('webshopSettings.paymentSetupRequired') }}</h3>
        <p class="payment-setup-message">{{ t('webshopSettings.paymentSetupMessage') }}</p>
      </div>

      <!-- Current Payment Method - Show if in trial/expired and has renewal card -->
      <div v-if="(isExpiredOrFailed || (isInTrial && showTrialPlanSelectionState)) && renewalCard" class="payment-method-section">
        <h3 class="payment-method-title">{{ t('webshopSettings.currentPaymentMethod') }}</h3>
        <div class="payment-card-display">
          <div class="card-info">
            <div class="card-number">{{ renewalCard.accountNumber }}</div>
            <div class="card-expiry">{{ t('webshopSettings.expires') }} {{ formatCardExpiration(renewalCard.expirationDate) }}</div>
          </div>
        </div>
      </div>

      <!-- Action Buttons (Order: Not Enrolled → Expired → Trial → Premium) -->
      <div class="action-buttons">
        <!-- Not Enrolled: Show Start Free Trial button -->
        <button 
          v-if="isNotEnrolled" 
          class="btn-primary btn-full-width" 
          :disabled="isProcessing"
          @click="handleStartFreeTrial"
        >
          {{ isProcessing ? t('webshopSettings.processing') : t('webshopSettings.startFreeTrial') }}
        </button>

        <!-- Retry Pending (invalid payment method): only Manage Billing to add/change the card -->
        <button
          v-else-if="isRetryPending"
          class="btn-primary btn-full-width"
          :disabled="isProcessing"
          @click="handleManageBilling"
        >
          {{ t('webshopSettings.manageBilling') }}
        </button>

        <!-- Expired with payment method: Show Manage Billing + Re-enroll buttons -->
        <template v-else-if="isExpiredOrFailed && renewalCard">
          <button 
            class="btn-secondary" 
            :disabled="isProcessing || isPendingRenewalProcessing"
            @click="handleManageBilling"
          >
            {{ t('webshopSettings.manageBilling') }}
          </button>
          <button 
            class="btn-primary" 
            :disabled="isProcessing || !selectedPlan || isPendingRenewalProcessing"
            @click="handleReenroll"
          >
            {{ isProcessing ? t('webshopSettings.processing') : t('webshopSettings.reenroll') }}
          </button>
        </template>

        <!-- Expired without payment method: Show Manage Billing button -->
        <button 
          v-else-if="isExpiredOrFailed && !renewalCard"
          class="btn-primary btn-full-width" 
          :disabled="isProcessing || isPendingRenewalProcessing"
          @click="handleManageBilling"
        >
          {{ t('webshopSettings.manageBilling') }}
        </button>

        <!-- In Trial with pending change: Show Cancel Pending Change + Manage Billing buttons -->
        <template v-else-if="isInTrial && hasPendingChange && renewalCard">
          <button 
            class="btn-warning" 
            :disabled="isProcessing"
            @click="handleOpenCancelPendingModal"
          >
            {{ t('webshopSettings.cancelPendingChange') }}
          </button>
          <button
            v-if="showTrialPlanSelectionState"
            class="btn-secondary"
            :disabled="isProcessing"
            @click="handleManageBilling"
          >
            {{ t('webshopSettings.manageBilling') }}
          </button>
        </template>

        <!-- In Trial with renewal card (no pending): Show Manage Billing button -->
        <button
          v-else-if="isInTrial && renewalCard && showTrialPlanSelectionState"
          class="btn-secondary btn-full-width" 
          :disabled="isProcessing"
          @click="handleManageBilling"
        >
          {{ t('webshopSettings.manageBilling') }}
        </button>

        <!-- In Trial without renewal card: Show Continue to Purchase button -->
        <button
          v-else-if="isInTrial && !renewalCard && showTrialPlanSelectionState"
          class="btn-primary btn-full-width" 
          :disabled="isProcessing"
          @click="handleContinuePurchase"
        >
          {{ isProcessing ? t('webshopSettings.processing') : t('webshopSettings.continueToPurchase') }}
        </button>

        <!-- Premium with pending change: Show Cancel Pending Change + Manage Billing buttons (NO Cancel Subscription) -->
        <template v-else-if="isPremium && hasPendingChange">
          <button 
            class="btn-warning" 
            :disabled="isProcessing"
            @click="handleOpenCancelPendingModal"
          >
            {{ t('webshopSettings.cancelPendingChange') }}
          </button>
          <button 
            class="btn-secondary" 
            :disabled="isProcessing"
            @click="handleManageBilling"
          >
            {{ t('webshopSettings.manageBilling') }}
          </button>
        </template>

        <!-- Premium without pending change and NOT cancelled: Show Manage Billing + Cancel Subscription buttons -->
        <template v-else-if="isPremium && !isCancelled">
          <button 
            class="btn-secondary" 
            :disabled="isProcessing"
            @click="handleManageBilling"
          >
            {{ t('webshopSettings.manageBilling') }}
          </button>
          <button 
            class="btn-danger" 
            :disabled="isProcessing"
            @click="handleOpenCancelSubscriptionModal"
          >
            {{ t('webshopSettings.cancelSubscription') }}
          </button>
        </template>

        <!-- Premium cancelled: Show only Manage Billing button -->
        <button 
          v-else-if="isPremium && isCancelled" 
          class="btn-secondary btn-full-width" 
          :disabled="isProcessing"
          @click="handleManageBilling"
        >
          {{ t('webshopSettings.manageBilling') }}
        </button>
      </div>
      
      <!-- Error Message -->
      <div v-if="actionError" class="error-message">
        {{ actionError }}
      </div>
    </div>

    <!-- Billing Management Modal -->
    <Dialog
      v-if="showBillingModal"
      :title="t('webshopSettings.manageBilling')"
      @close="handleCloseBillingModal"
      :width="600"
    >
      <div class="billing-modal-content">
        <!-- Add Card Form -->
        <div v-if="showAddCardForm" class="add-card-section">
          <h4>{{ t('webshopSettings.addNewCard') }}</h4>
          <div v-if="addingCard" class="processing-overlay">
            <Loader type="infinite-spinner" :size="'medium'" />
            <p>{{ t('webshopSettings.processingCard') }}</p>
          </div>
          <BraintreeWalletEntry 
            @wallet-added="handleCardAdded" 
            @loading="handleBraintreeLoading"
          />
          <button 
            class="btn-secondary" 
            @click="showAddCardForm = false"
            :disabled="addingCard"
          >
            {{ t('webshopSettings.cancel') }}
          </button>
        </div>

        <!-- Cards List -->
        <div v-else class="cards-list-section">
          <h4>{{ t('webshopSettings.selectPaymentMethod') }}</h4>
          
          <!-- Card Items -->
          <div class="modal-cards-list">
            <div 
              v-for="card in walletCards" 
              :key="card.cusWalletGuid"
              class="modal-card-item"
              :class="{ 'is-renewal': renewalCard && card.cusWalletGuid === renewalCard.cusWalletGuid }"
            >
              <div class="modal-card-info">
                <div class="modal-card-number">{{ card.accountNumber }}</div>
                <div class="modal-card-holder">{{ card.accountHolderName }}</div>
                <div class="modal-card-expiry">{{ t('webshopSettings.expires') }} {{ formatCardExpiration(card.expirationDate) }}</div>
              </div>
              <div class="card-actions">
                <button 
                  v-if="!renewalCard || card.cusWalletGuid !== renewalCard.cusWalletGuid"
                  class="btn-select-card"
                  @click="handleSelectCard(card.cusWalletGuid)"
                  :disabled="isProcessing"
                >
                  {{ t('webshopSettings.select') }}
                </button>
                <div v-else class="current-badge">
                  {{ t('webshopSettings.current') }}
                </div>
                <button
                  v-if="!renewalCard || card.cusWalletGuid !== renewalCard.cusWalletGuid"
                  class="btn-delete-card"
                  @click="openDeleteCardConfirmation(card.cusWalletGuid)"
                  :disabled="isProcessing"
                >
                  {{ t('buttons.Delete') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Add Card Button -->
          <button 
            class="btn-add-card"
            @click="showAddCardForm = true"
            :disabled="isProcessing || addingCard"
          >
            <i class="fas fa-plus"></i>
            {{ t('webshopSettings.addNewCard') }}
          </button>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-if="showDeleteCardModal"
      :title="t('webshopSettings.deleteCardTitle')"
      @close="handleCloseDeleteCardModal"
      :width="450"
    >
      <div class="confirmation-modal-content">
        <p class="confirmation-message">
          {{ t('webshopSettings.deleteCardMessage') }}
        </p>

        <div class="confirmation-actions">
          <button
            class="btn-secondary"
            @click="handleCloseDeleteCardModal"
            :disabled="isProcessing"
          >
            {{ t('webshopSettings.cancel') }}
          </button>
          <button
            class="btn-danger"
            @click="handleConfirmDeleteCard"
            :disabled="isProcessing"
          >
            {{ isProcessing ? t('webshopSettings.processing') : t('webshopSettings.confirmDeleteCard') }}
          </button>
        </div>
      </div>
    </Dialog>

    <!-- Plan Confirmation Modal -->
    <Dialog
      v-if="showPlanConfirmation"
      :title="t('webshopSettings.confirmPlanSelection')"
      @close="handleCancelPlanSelection"
      :width="450"
    >
      <div class="confirmation-modal-content">
        <p class="confirmation-message">
          {{ t('webshopSettings.confirmPlanMessage') }}
        </p>
        
        <div v-if="pendingPlanSelection && plans[pendingPlanSelection]" class="selected-plan-summary">
          <h4>{{ getPlanNameLabel(pendingPlanSelection) }}</h4>
          <div class="summary-price">{{ formatEuroPrice(plans[pendingPlanSelection].price) }}</div>
          <p class="summary-frequency">{{ plans[pendingPlanSelection].frequency }}</p>
        </div>

        <div class="confirmation-actions">
          <button 
            class="btn-secondary"
            @click="handleCancelPlanSelection"
            :disabled="isProcessing"
          >
            {{ t('webshopSettings.cancel') }}
          </button>
          <button 
            class="btn-primary"
            @click="handleConfirmPlanSelection"
            :disabled="isProcessing"
          >
            {{ isProcessing ? t('webshopSettings.processing') : t('webshopSettings.confirm') }}
          </button>
        </div>
      </div>
    </Dialog>

    <!-- Cancel Pending Change Confirmation Modal -->
    <Dialog
      v-if="showCancelPendingModal"
      :title="t('webshopSettings.cancelPendingChangeTitle')"
      @close="handleCloseCancelPendingModal"
      :width="450"
    >
      <div class="confirmation-modal-content">
        <p class="confirmation-message">
          {{ t('webshopSettings.cancelPendingChangeMessage') }}
        </p>

        <div class="confirmation-actions">
          <button 
            class="btn-secondary"
            @click="handleCloseCancelPendingModal"
            :disabled="isProcessing"
          >
            {{ t('webshopSettings.cancel') }}
          </button>
          <button 
            class="btn-warning"
            @click="handleConfirmCancelPending"
            :disabled="isProcessing"
          >
            {{ isProcessing ? t('webshopSettings.processing') : t('webshopSettings.confirmCancel') }}
          </button>
        </div>
      </div>
    </Dialog>

    <!-- Cancel Subscription Confirmation Modal -->
    <Dialog
      v-if="showCancelSubscriptionModal"
      :title="t('webshopSettings.cancelSubscriptionTitle')"
      @close="handleCloseCancelSubscriptionModal"
      :width="450"
    >
      <div class="confirmation-modal-content">
        <p class="confirmation-message">
          {{ t('webshopSettings.cancelSubscriptionMessage') }}
        </p>

        <div class="confirmation-actions">
          <button 
            class="btn-secondary"
            @click="handleCloseCancelSubscriptionModal"
            :disabled="isProcessing"
          >
            {{ t('webshopSettings.cancel') }}
          </button>
          <button 
            class="btn-danger"
            @click="handleConfirmCancelSubscription"
            :disabled="isProcessing"
          >
            {{ isProcessing ? t('webshopSettings.processing') : t('webshopSettings.confirmCancelSubscription') }}
          </button>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Loader } from '@progress/kendo-vue-indicators'
import { Dialog } from '@progress/kendo-vue-dialogs'
import BraintreeWalletEntry from '../PaymentGateways/Braintree/BraintreeWalletEntry.vue'
import { TpcRenewalStatusGet, RenewalChangePlan, RenewalProgramsGet, TpcRenewalRevertPendingChanges, RenewalEnroll, RenewalCancel } from '../../api/ConsultantCorner'
import { WalletDelete, WalletGetListWithGuidSupport, WalletUpdateUseForType } from '../../api/Order'
import type { ITpcRenewalStatus, IRenewalFrequency } from '../../types/ConsultantCornerTypes'
import type { IWalletItem } from '../../types/OrderTypes'
import { useStoreLogger } from '../../logger'

const { t, locale } = useI18n()
const logger = useStoreLogger()

// Loading state
const isLoading = ref(true)
const loadError = ref(false)

// Action processing state
const isProcessing = ref(false)
const actionError = ref<string | null>(null)

// Renewal status from API
const renewalStatus = ref<ITpcRenewalStatus | null>(null)

// Wallet/Billing state
const walletCards = ref<IWalletItem[]>([])
const showBillingModal = ref(false)
const showAddCardForm = ref(false)
const addingCard = ref(false)
const showDeleteCardModal = ref(false)
const pendingDeleteCardGuid = ref<string | null>(null)

// Plan confirmation state
const showPlanConfirmation = ref(false)
const pendingPlanSelection = ref<string | null>(null)

// Cancel pending change state
const showCancelPendingModal = ref(false)

// Cancel subscription state
const showCancelSubscriptionModal = ref(false)

// Available frequencies from API
const availableFrequencies = ref<IRenewalFrequency[]>([])

// Selected plan
const selectedPlan = ref<string | null>(null)

// Helper function to get frequency label
const getFrequencyLabel = (frequencyId: string): string => {
  const normalized = frequencyId.toUpperCase()
  switch (normalized) {
    case 'MONTHLY':
      return t('webshopSettings.frequency.perMonth')
    case 'QUARTERLY':
      return t('webshopSettings.frequency.everyThreeMonths')
    case 'BIANNUALLY':
      // Legacy — no longer selectable, kept for existing subscribers
      return t('webshopSettings.frequency.everySixMonths')
    case 'ANNUALLY':
      return t('webshopSettings.frequency.perYear')
    default:
      return frequencyId
  }
}

// Helper function to get the localized plan name for a normalized frequency id
// (monthly/quarterly/biannual/annual). 'biannual' is legacy — kept so existing
// subscribers still see a correct plan name.
const getPlanNameLabel = (normalizedId: string | null): string => {
  switch (normalizedId) {
    case 'monthly':
      return t('webshopSettings.monthly')
    case 'quarterly':
      return t('webshopSettings.quarterly')
    case 'biannual':
      return t('webshopSettings.biAnnual')
    case 'annual':
      return t('webshopSettings.annual')
    default:
      return ''
  }
}

const handleDeleteCard = async (cusWalletGuid: string) => {
  try {
    isProcessing.value = true
    actionError.value = null

    logger.info('Deleting wallet card...', { cusWalletGuid })

    const response = await WalletDelete({ cusWalletGuid })
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || t('alerts.UnknownError'))
    }

    await loadWalletCards()
  } catch (error) {
    logger.error('Error deleting card:', error)
    actionError.value = error instanceof Error ? error.message : t('alerts.UnknownError')
  } finally {
    isProcessing.value = false
  }
}

const openDeleteCardConfirmation = (cusWalletGuid: string) => {
  pendingDeleteCardGuid.value = cusWalletGuid
  showDeleteCardModal.value = true
}

const handleCloseDeleteCardModal = () => {
  showDeleteCardModal.value = false
  pendingDeleteCardGuid.value = null
}

const handleConfirmDeleteCard = async () => {
  if (!pendingDeleteCardGuid.value) return

  await handleDeleteCard(pendingDeleteCardGuid.value)
  handleCloseDeleteCardModal()
}

// Helper function to normalize frequency ID for internal use
const normalizeFrequencyId = (frequencyId: string): string => {
  const normalized = frequencyId.toUpperCase()
  switch (normalized) {
    case 'MONTHLY':
      return 'monthly'
    case 'QUARTERLY':
      return 'quarterly'
    case 'BIANNUALLY':
      // Legacy — no longer selectable, kept so existing subscribers still resolve correctly
      return 'biannual'
    case 'ANNUALLY':
      return 'annual'
    default:
      return frequencyId.toLowerCase()
  }
}

// Helper function to get original frequency ID for API (MONTHLY, QUARTERLY, BIANNUALLY, ANNUALLY)
const getOriginalFrequencyId = (normalizedId: string): string => {
  const plan = plans.value[normalizedId]
  return plan?.frequencyId || normalizedId.toUpperCase()
}

// Computed plans based on available frequencies
const plans = computed(() => {
  const plansMap: Record<string, { price: number; frequency: string; frequencyId: string }> = {}
  
  availableFrequencies.value.forEach(freq => {
    const normalizedId = normalizeFrequencyId(freq.frequencyId)
    plansMap[normalizedId] = {
      price: freq.price,
      frequency: getFrequencyLabel(freq.frequencyId),
      frequencyId: freq.frequencyId
    }
  })
  
  return plansMap
})

// Computed properties based on status
const isNotEnrolled = computed(() => {
  return renewalStatus.value?.status?.toLowerCase() === 'not enrolled'
})

const isInTrial = computed(() => {
  return renewalStatus.value?.programId?.toLowerCase() === 'trial'
})

const isTrialPlanSelectionWindowOpen = computed(() => {
  if (!isInTrial.value || !renewalStatus.value?.renewalDate) return false

  const renewalDate = new Date(renewalStatus.value.renewalDate)
  const currentDate = new Date()
  const tenDaysBeforeRenewal = new Date(renewalDate)
  tenDaysBeforeRenewal.setDate(tenDaysBeforeRenewal.getDate() - 10)

  return currentDate >= tenDaysBeforeRenewal
})

const trialSelectionCountdownText = computed(() => {
  if (!isInTrial.value || !renewalStatus.value?.renewalDate) return ''

  const renewalDate = new Date(renewalStatus.value.renewalDate)
  const currentDate = new Date()
  
  const tenDaysBeforeRenewal = new Date(renewalDate)
  tenDaysBeforeRenewal.setDate(tenDaysBeforeRenewal.getDate() - 10)
  
  const timeDiff = tenDaysBeforeRenewal.getTime() - currentDate.getTime()
  const daysUntilEnabled = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
  
  if (daysUntilEnabled <= 0) return ''
  
  if (daysUntilEnabled > 7) {
    const weeks = Math.round(daysUntilEnabled / 7)
    return weeks === 1
      ? t('webshopSettings.countdownInOneWeek')
      : t('webshopSettings.countdownInWeeks', { count: weeks })
  } else {
    return daysUntilEnabled === 1
      ? t('webshopSettings.countdownInOneDay')
      : t('webshopSettings.countdownInDays', { count: daysUntilEnabled })
  }
})

const isExpired = computed(() => {
  if (!renewalStatus.value?.renewalDate) return false
  const renewalDate = new Date(renewalStatus.value.renewalDate)
  const currentDate = new Date()
  return renewalDate < currentDate
})

// Payment method invalid / retry pending: the renewal charge failed (or there is no valid card)
// and the system is retrying. The subscription stays active; the consultant must add or change
// their payment method. Plan selection is locked in this state and only Manage Billing is allowed.
const isRetryPending = computed(() => {
  return renewalStatus.value?.status?.toLowerCase() === 'retrypending'
})

// FAILED means the retry cycle is exhausted: the subscription is inactive and the consultant
// can re-enroll. It presents exactly like the Expired state, for both Trial and Premium.
const isFailed = computed(() => {
  return renewalStatus.value?.status?.toLowerCase() === 'failed'
})

// Presentation gate for the inactive-subscription state. isExpired is date-based while FAILED is
// status-based; this is a superset of isExpired, so every other status behaves exactly as before.
const isExpiredOrFailed = computed(() => {
  return isExpired.value || isFailed.value
})

// Check if subscription is cancelled
const isCancelled = computed(() => {
  return renewalStatus.value?.status?.toLowerCase() === 'cancelled'
})

const trialEndDate = computed(() => {
  if (!renewalStatus.value?.renewalDate) return ''
  return new Date(renewalStatus.value.renewalDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
})

const renewalUsageKeywords = [
  'renewal',
  'verlang',
  'renouvel',
]

const normalizeUsageText = (value: string): string => {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

const hasRenewalUsage = (usage: string | string[] | null | undefined): boolean => {
  if (!usage) return false

  const usageValues = Array.isArray(usage) ? usage : [usage]

  return usageValues.some(usageValue => {
    const normalizedUsage = normalizeUsageText(usageValue)
    const usageTokens = normalizedUsage
      .split(',')
      .map(token => token.trim())
      .filter(Boolean)

    return usageTokens.some(token => renewalUsageKeywords.some(keyword => token.includes(keyword)))
  })
}

// Get the renewal card (first card with usage containing Renewal in supported locales)
const renewalCard = computed(() => {
  return walletCards.value.find(card => hasRenewalUsage(card.usage))
})

const formatEuroPrice = (value: number | string): string => {
  const parsedValue = typeof value === 'number' ? value : Number(value)
  const amount = Number.isFinite(parsedValue) ? parsedValue : 0

  return new Intl.NumberFormat(locale.value || 'en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

// Format card expiration date
const formatCardExpiration = (expirationDate: string | Date): string => {
  const date = typeof expirationDate === 'string' ? new Date(expirationDate) : expirationDate
  return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(-2)}`
}

// Show plan selection prompt (after user has set up payment method)
const showPlanSelectionPrompt = computed(() => {
  return (isInTrial.value || isPremium.value) && renewalCard.value
})

// Check if user is on Premium plan
const isPremium = computed(() => {
  return renewalStatus.value?.programId?.toLowerCase() === 'premium'
})

// Get current plan ID (normalized)
const currentPlanId = computed(() => {
  if (!renewalStatus.value?.frequency) return null
  return normalizeFrequencyId(renewalStatus.value.frequency)
})

// Get pending plan ID (normalized)
const pendingPlanId = computed(() => {
  if (!renewalStatus.value?.futureFrequencyPlan) return null
  // Once the renewal has FAILED, any futureFrequencyPlan left over is stale: the subscription is
  // inactive, so nothing should render as a pending plan change.
  if (isFailed.value) return null
  return normalizeFrequencyId(renewalStatus.value.futureFrequencyPlan)
})

// Trial users carried over from the old PWS subscription flow can already have a future
// plan selected before the 10-day selection window opens. Derived from futureFrequencyPlan
// (not pendingPlanDetails) so a legacy frequency that no longer resolves against `plans`
// still counts as a preselection.
const hasPreselectedTrialPlan = computed(() => {
  return isInTrial.value && !!pendingPlanId.value
})

// Presentation gate for the Trial state: renders the full state (plans grid, payment method,
// billing buttons) either inside the 10-day window or whenever a plan is already preselected.
// The 10-day business rule in isTrialPlanSelectionWindowOpen is unchanged for everyone else.
const showTrialPlanSelectionState = computed(() => {
  return isTrialPlanSelectionWindowOpen.value || hasPreselectedTrialPlan.value
})

const isTrialWithoutRenewalCard = computed(() => {
  return isInTrial.value && showTrialPlanSelectionState.value && !renewalCard.value && !isExpired.value && !isFailed.value
})

// Check if there's a pending plan change
const hasPendingChange = computed(() => {
  if (!renewalStatus.value?.futureFrequencyPlan) return false
  // A failed renewal leaves no pending change to act on.
  if (isFailed.value) return false
  // During Trial, `frequency` isn't a real "current plan" to compare against
  // (it can coincidentally match futureFrequencyPlan, e.g. both 'QUARTERLY'),
  // so any futureFrequencyPlan present already means a renewal is pending.
  if (isInTrial.value) return true
  return renewalStatus.value.futureFrequencyPlan !== renewalStatus.value.frequency
})

const isPendingRenewalProcessing = computed(() => {
  const status = renewalStatus.value?.status?.toLowerCase()

  // FAILED is a terminal state, never "still processing".
  if (isFailed.value) return false

  return isExpired.value && (
    (status === 'goodstanding' && !isInTrial.value) ||
    (isInTrial.value && hasPendingChange.value)
  )
})

// Get pending plan details for Trial state
const pendingPlanDetails = computed(() => {
  if (!isInTrial.value || !pendingPlanId.value) return null
  
  const plan = plans.value[pendingPlanId.value]
  if (!plan) return null
  
  const planName = getPlanNameLabel(pendingPlanId.value)
  
  return {
    name: planName,
    price: plan.price,
    frequency: plan.frequency
  }
})

// Get current plan details for Premium state
const currentPlanDetails = computed(() => {
  if (!isPremium.value || !currentPlanId.value) return null
  
  const plan = plans.value[currentPlanId.value]
  if (!plan) return null
  
  const planName = getPlanNameLabel(currentPlanId.value)
  
  return {
    name: planName,
    price: plan.price,
    frequency: plan.frequency
  }
})

// Format renewal date
const nextBillingDate = computed(() => {
  if (!renewalStatus.value?.renewalDate) return null
  const date = new Date(renewalStatus.value.renewalDate)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
})

// Load wallet cards from API
const loadWalletCards = async () => {
  try {
    logger.info('Loading wallet cards...')
    const response = await WalletGetListWithGuidSupport()
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to load wallet cards')
    }
    
    if (response.items) {
      walletCards.value = response.items
      logger.info('Wallet cards loaded:', response.items.length, 'cards')
    } else {
      walletCards.value = []
      logger.warn('No wallet cards returned')
    }
  } catch (error) {
    logger.error('Error loading wallet cards:', error)
    // Don't throw - wallet cards are optional
  }
}

// Load renewal programs from API
const loadRenewalPrograms = async () => {
  try {
    logger.info('Loading renewal programs...')
    const response = await RenewalProgramsGet()
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to load renewal programs')
    }
    
    if (response.item?.frequencies) {
      availableFrequencies.value = response.item.frequencies
      logger.info('Renewal programs loaded:', response.item)
    } else {
      logger.warn('No renewal programs data returned')
    }
  } catch (error) {
    logger.error('Error loading renewal programs:', error)
    throw error
  }
}

// Load renewal status from API
const loadRenewalStatus = async () => {
  try {
    isLoading.value = true
    loadError.value = false
    
    logger.info('Loading renewal status...')
    const response = await TpcRenewalStatusGet()
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to load renewal status')
    }
    
    if (response.item) {
      renewalStatus.value = response.item
      logger.info('Renewal status loaded:', response.item)
      
      // For expired (date-based) or failed, require explicit user plan selection before reenroll
      if (isExpiredOrFailed.value) {
        selectedPlan.value = null
      } else if (response.item.frequency) {
        selectedPlan.value = normalizeFrequencyId(response.item.frequency)
      }
    } else {
      logger.warn('No renewal status data returned')
    }
  } catch (error) {
    logger.error('Error loading renewal status:', error)
    loadError.value = true
  } finally {
    isLoading.value = false
  }
}

// Action handlers
// Architecture: Each button handler follows this pattern:
// 1. Set isProcessing = true and clear actionError
// 2. Call appropriate API (RenewalChangePlan or other)
// 3. Handle response and errors
// 4. Reload renewalStatus to update UI
// 5. Set isProcessing = false in finally block

const handleStartFreeTrial = async () => {
  try {
    isProcessing.value = true
    actionError.value = null
    
    logger.info('Starting free trial...', {
      status: renewalStatus.value?.status
    })
    
    const response = await RenewalChangePlan({
      newProgramId: 'Trial',
      frequency: undefined
    })
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to start free trial')
    }
    
    logger.info('Free trial started successfully')
    
    // Reload renewal status to get updated data
    await loadRenewalStatus()
    
  } catch (error) {
    logger.error('Error starting free trial:', error)
    actionError.value = error instanceof Error ? error.message : 'Failed to start free trial'
  } finally {
    isProcessing.value = false
  }
}

const handleContinuePurchase = async () => {
  // Open billing modal to select/manage payment method
  showBillingModal.value = true
}

const handleManageBilling = () => {
  showBillingModal.value = true
}

const handleSelectCard = async (cusWalletGuid: string) => {
  try {
    isProcessing.value = true
    actionError.value = null
    
    logger.info('Selecting card for renewal...', { cusWalletGuid })
    
    // First, update the current renewal card to 'cardonfile' if it exists
    if (renewalCard.value && renewalCard.value.cusWalletGuid !== cusWalletGuid) {
      logger.info('Updating previous renewal card to cardonfile...', { 
        previousCusWalletGuid: renewalCard.value.cusWalletGuid 
      })
      
      const previousCardResponse = await WalletUpdateUseForType({
        cusWalletGuid: renewalCard.value.cusWalletGuid,
        useForType: 'cardonfile'
      })
      
      if (previousCardResponse.responseStatus?.errorCode && previousCardResponse.responseStatus.errorCode !== '0') {
        throw new Error(previousCardResponse.responseStatus.message || 'Failed to update previous card')
      }
      
      logger.info('Previous renewal card updated to cardonfile successfully')
    }
    
    // Now set the new card as renewal
    const response = await WalletUpdateUseForType({
      cusWalletGuid: cusWalletGuid,
      useForType: 'Renewal'
    })
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to update card')
    }
    
    logger.info('Card updated successfully')
    
    // Reload wallet cards to refresh the UI
    await loadWalletCards()
    
    // Close modal
    showBillingModal.value = false
    
  } catch (error) {
    logger.error('Error selecting card:', error)
    actionError.value = error instanceof Error ? error.message : 'Failed to update card'
  } finally {
    isProcessing.value = false
  }
}

const handleCardAdded = async () => {
  // Refresh wallet cards after adding a new card
  await loadWalletCards()
  showAddCardForm.value = false
}

const handleBraintreeLoading = (loading: boolean) => {
  addingCard.value = loading
}

const handleCloseBillingModal = () => {
  showBillingModal.value = false
  showAddCardForm.value = false
  handleCloseDeleteCardModal()
}

const handlePlanClick = (planId: string) => {
  // Expired / failed state: Select plan (don't open modal, just select visually)
  if (isExpiredOrFailed.value) {
    selectedPlan.value = planId
    return
  }
  
  // If user has renewal card, show confirmation modal
  if (showPlanSelectionPrompt.value) {
    pendingPlanSelection.value = planId
    showPlanConfirmation.value = true
  } else {
    // Otherwise just select the plan visually
    selectedPlan.value = planId
  }
}

const handleReenroll = () => {
  if (!selectedPlan.value || !isExpiredOrFailed.value) return

  pendingPlanSelection.value = selectedPlan.value
  showPlanConfirmation.value = true
}

const handleConfirmPlanSelection = async () => {
  if (!pendingPlanSelection.value) return
  
  try {
    isProcessing.value = true
    actionError.value = null
    
    const frequencyId = getOriginalFrequencyId(pendingPlanSelection.value)
    let response

    if (isExpiredOrFailed.value) {
      logger.info('Confirming reenroll with selected plan...', {
        plan: pendingPlanSelection.value,
        frequencyId: frequencyId
      })

      response = await RenewalEnroll({
        frequency: frequencyId
      })
    } else {
      logger.info('Confirming plan selection...', {
        plan: pendingPlanSelection.value,
        frequencyId: frequencyId
      })

      response = await RenewalChangePlan({
        newProgramId: 'Premium',
        frequency: frequencyId
      })
    }
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to change plan')
    }
    
    logger.info(isExpiredOrFailed.value ? 'Reenroll successful' : 'Plan changed successfully')
    
    // Update selected plan
    selectedPlan.value = pendingPlanSelection.value
    
    // Reload renewal status to get updated data
    await loadRenewalStatus()
    
    // Close confirmation modal
    showPlanConfirmation.value = false
    pendingPlanSelection.value = null
    
  } catch (error) {
    logger.error('Error changing plan:', error)
    actionError.value = error instanceof Error ? error.message : 'Failed to change plan'
  } finally {
    isProcessing.value = false
  }
}

const handleCancelPlanSelection = () => {
  showPlanConfirmation.value = false
  pendingPlanSelection.value = null
}

const handleOpenCancelPendingModal = () => {
  showCancelPendingModal.value = true
}

const handleCloseCancelPendingModal = () => {
  showCancelPendingModal.value = false
}

const handleConfirmCancelPending = async () => {
  try {
    isProcessing.value = true
    actionError.value = null
    
    logger.info('Reverting pending plan changes...')
    
    const response = await TpcRenewalRevertPendingChanges()
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to revert pending changes')
    }
    
    logger.info('Pending changes reverted successfully')
    
    // Reload renewal status to get updated data
    await loadRenewalStatus()
    
    // Close modal
    showCancelPendingModal.value = false
    
  } catch (error) {
    logger.error('Error reverting pending changes:', error)
    actionError.value = error instanceof Error ? error.message : 'Failed to revert pending changes'
  } finally {
    isProcessing.value = false
  }
}

const handleOpenCancelSubscriptionModal = () => {
  showCancelSubscriptionModal.value = true
}

const handleCloseCancelSubscriptionModal = () => {
  showCancelSubscriptionModal.value = false
}

const handleConfirmCancelSubscription = async () => {
  try {
    isProcessing.value = true
    actionError.value = null
    
    logger.info('Cancelling subscription...')
    
    const response = await RenewalCancel()
    
    if (response.responseStatus?.errorCode && response.responseStatus.errorCode !== '0') {
      throw new Error(response.responseStatus.message || 'Failed to cancel subscription')
    }
    
    logger.info('Subscription cancelled successfully')
    
    // Reload renewal status to get updated data
    await loadRenewalStatus()
    
    // Close modal
    showCancelSubscriptionModal.value = false
    
  } catch (error) {
    logger.error('Error cancelling subscription:', error)
    actionError.value = error instanceof Error ? error.message : 'Failed to cancel subscription'
  } finally {
    isProcessing.value = false
  }
}

// Lifecycle
onMounted(async () => {
  try {
    // Load all data in parallel where possible
    await Promise.all([
      loadRenewalPrograms(),
      loadWalletCards()
    ])
    await loadRenewalStatus()
  } catch (error) {
    logger.error('Error during component initialization:', error)
    loadError.value = true
    isLoading.value = false
  }
})
</script>

<style lang="scss" scoped>
/* Main Container */
.ccma-webshop-settings {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 1.5rem;
}

/* Loading overlay styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(white, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-loading-overlay;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}




/* Plan Selection Container */
.plan-selection-container {
  background: white;
  border-radius: $radius-xl;
  padding: 2rem 1.75rem;
  max-width: 700px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(black, 0.1);
  text-align: center;
}

/* Trial Badge */
.trial-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  background: #fff3cd;
  color: #856404;
  padding: 0.5rem 1.25rem;
  border-radius: $radius-2xl;
  margin-bottom: 1rem;
}

/* Active Subscription Badge */
.active-subscription-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  background: #d4edda;
  color: #155724;
  padding: 0.5rem 1.25rem;
  border-radius: $radius-2xl;
  margin-bottom: 1rem;
}

/* No Active Subscription Badge */
.no-active-subscription-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  background: #f8d7da;
  color: #721c24;
  padding: 0.5rem 1.25rem;
  border-radius: $radius-2xl;
  margin-bottom: 1rem;
}

/* Main Title */
.main-title {
  @include typography-h3;
  color: #2d3436;
  margin: 0 0 0.7rem 0;
  text-transform: none;
}

/* Alert Box */
.alert-box {
  background: #fff5f5;
  border: 2px solid #fc8181;
  border-radius: $radius-lg;
  padding: 1.25rem 1.5rem;
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.alert-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #c53030;
  margin: 0 0 0.5rem 0;
}

.alert-message {
  @include typography-body;
  color: #718096;
  margin: 0;
}

/* Trial Info */
.trial-info {
  @include typography-body;
  color: #636e72;
  margin: 0 0 1.75rem 0;
}

.trial-date {
  @include font-weight-semibold;
  color: #d32f2f;
}

/* Expired State Messages */
.expired-messages {
  text-align: center;
  margin-bottom: 1.5rem;
}

.get-started-text {
  @include typography-body;
  color: #636e72;
  margin: 0 0 0.5rem 0;
}

.choose-plan-text {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #c53030;
  margin: 0;
}

/* Plan Selection Prompt */
.plan-selection-prompt {
  @include typography-body;
  @include font-weight-semibold;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  margin: 0 0 1.5rem 0;
  text-align: center;
}

/* Pending Change Alert */
.pending-change-alert {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
}

.alert-icon {
  @include typography-h3;
}

.alert-content {
  flex: 1;
}

.alert-content strong {
  @include typography-body;
  @include font-weight-bold;
  display: block;
  color: #856404;
  margin-bottom: 0.25rem;
}

.alert-content p {
  @include typography-body;
  color: #856404;
  margin: 0;
}

/* Current Selection Box */
.current-selection-box {
  background: #e3f2fd;
  border: 2px solid #2196f3;
  border-radius: $radius-md;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.selection-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #1565c0;
  margin: 0 0 0.75rem 0;
}

.selection-plan {
  @include typography-body;
  @include font-weight-semibold;
  color: #0d47a1;
  margin-bottom: 0.5rem;
}

.selection-message {
  @include typography-small;
  color: #1976d2;
  margin: 0;
}

/* No Plan Selected Alert */
.no-plan-alert {
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: $radius-md;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.no-plan-alert strong {
  @include typography-body;
  @include font-weight-bold;
  display: block;
  color: #856404;
  margin-bottom: 0.5rem;
}

.no-plan-alert p {
  @include typography-body;
  color: #856404;
  margin: 0;
}

/* No Subscription Alert (Expired State) */
.no-subscription-alert {
  background: #fff5f5;
  border: 2px solid #fc8181;
  border-radius: $radius-lg;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.no-subscription-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #c53030;
  margin: 0 0 0.5rem 0;
}

.no-subscription-message {
  @include typography-body;
  color: #718096;
  margin: 0;
}

/* Invalid Payment Method Alert (Retry Pending State) */
.invalid-payment-alert {
  background: #fff5f5;
  border: 2px solid #fc8181;
  border-radius: $radius-lg;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.invalid-payment-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #c53030;
  margin: 0 0 0.5rem 0;
}

.invalid-payment-message {
  @include typography-body;
  color: #718096;
  margin: 0;
}

.invalid-payment-date {
  @include typography-body;
  color: #718096;
  margin: 0.5rem 0 0 0;
}

.invalid-payment-date-value {
  @include font-weight-semibold;
  color: #c53030;
}

/* Current Plan Info Box (with pending change) */
.current-plan-info-box {
  background: var(--vt-c-white-soft);
  border: 2px solid #dee2e6;
  border-radius: $radius-md;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.current-plan-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #2d3436;
  margin: 0 0 0.5rem 0;
}

.current-plan-message {
  @include typography-small;
  color: #636e72;
  margin: 0;
}

/* Current Plan Info Box Simple (without pending change) */
.current-plan-info-box-simple {
  background: #fff5f5;
  border: 2px solid #ff6b6b;
  border-radius: $radius-md;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.current-plan-title-simple {
  @include typography-body-large;
  @include font-weight-bold;
  color: #c92a2a;
  margin: 0 0 0.5rem 0;
}

.current-plan-message-simple {
  @include typography-small;
  color: #a61e4d;
  margin: 0;
}

/* Cancelled Subscription Box */
.cancelled-subscription-box {
  background: #f8d7da;
  border: 2px solid #dc3545;
  border-radius: $radius-md;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.cancelled-subscription-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #721c24;
  margin: 0 0 0.5rem 0;
}

.cancelled-subscription-message {
  @include typography-small;
  color: #721c24;
  margin: 0;
}

/* Disabled Plan Card */
.plan-card.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.plan-card.is-disabled:hover {
  transform: none;
  box-shadow: none;
  border-color: #e0e0e0;
}

/* Subscription Status */
.subscription-status {
  text-align: center;
  margin-bottom: 1.5rem;
}

.status-active {
  @include typography-body;
  color: #636e72;
  margin: 0 0 0.5rem 0;
}

.next-billing {
  @include typography-body;
  color: #636e72;
  margin: 0;
}

.billing-date {
  @include font-weight-semibold;
  color: #00b894;
}

/* Plans Grid */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

/* Plan Card */
.plan-card {
  position: relative;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: $radius-lg;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}

.plan-card:hover {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(var(--vt-c-teal-dark), 0.15);
}

.plan-card.selected {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  background: #f0f7f8;
  box-shadow: 0 4px 12px rgba(var(--vt-c-teal-dark), 0.2);
}

.plan-card.is-current {
  border-color: #95a5a6;
  background: var(--vt-c-white-soft);
}

.plan-card.is-pending {
  border-color: #ffc107;
  background: #fff8f0;
}

/* Most Popular Badge */
.plan-card.most-popular {
  border-color: #00b894;
  border-width: 3px;
}

.popular-badge {
  @include typography-caption;
  @include font-weight-semibold;
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: #00b894;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: $radius-md;
  text-transform: uppercase;
}

/* Current Plan Badge */
.current-plan-badge {
  @include typography-caption;
  @include font-weight-semibold;
  position: absolute;
  top: -10px;
  right: 10px;
  background: #95a5a6;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: $radius-md;
  text-transform: uppercase;
}

/* Pending Plan Badge */
.pending-plan-badge {
  @include typography-caption;
  @include font-weight-semibold;
  position: absolute;
  top: -10px;
  right: 10px;
  background: #ffc107;
  color: #856404;
  padding: 0.3rem 0.8rem;
  border-radius: $radius-md;
  text-transform: uppercase;
}

/* Plan Content */
.plan-name {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #2d3436;
  margin: 0 0 0.7rem 0;
}

.plan-price {
  @include typography-h2;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  margin: 0.35rem 0;
}

.plan-frequency {
  @include typography-small;
  color: #636e72;
  margin: 0.35rem 0 0.7rem 0;
}

/* Payment Setup Info (Expired State) */
.payment-setup-info {
  margin: 1.5rem 0;
  padding: 1.25rem 1.5rem;
  background: var(--vt-c-white-soft);
  border: 2px solid #dee2e6;
  border-radius: $radius-lg;
  text-align: center;
}

.payment-setup-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #2d3436;
  margin: 0 0 0.5rem 0;
}

.payment-setup-message {
  @include typography-body;
  color: #636e72;
  margin: 0;
}

/* Payment Method Section */
.payment-method-section {
  margin: 2rem 0 1.5rem 0;
  padding: 1.5rem;
  background: var(--vt-c-white-soft);
  border-radius: $radius-lg;
}

.payment-method-title {
  @include typography-body-large;
  @include font-weight-bold;
  color: #2d3436;
  margin: 0 0 1rem 0;
}

.payment-card-display {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: $radius-md;
  border: 1px solid #e0e0e0;
}

.card-info {
  flex: 1;
}

.card-number {
  @include typography-body;
  @include font-weight-semibold;
  color: #2d3436;
  margin-bottom: 0.25rem;
}

.card-expiry {
  @include typography-small;
  color: #636e72;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 0;
}

.btn-primary,
.btn-secondary {
  padding: 0.65rem 1.75rem;
  border: none;
  border-radius: $radius-md;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
}

.btn-primary:hover {
  background: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(var(--vt-c-teal-dark), 0.4);
}

.btn-secondary {
  background: var(--vt-c-white-soft);
  color: #636e72;
  border: 2px solid #e0e0e0;
}

.btn-secondary:hover {
  background: #e9ecef;
  border-color: #636e72;
}

.btn-warning {
  padding: 0.65rem 1.75rem;
  border: none;
  border-radius: $radius-md;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #ffc107;
  color: #856404;
}

.btn-warning:hover {
  background: #e0a800;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(#ffc107, 0.4);
}

.btn-danger {
  padding: 0.65rem 1.75rem;
  border: none;
  border-radius: $radius-md;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(#dc2626, 0.4);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-full-width {
  width: 100%;
  max-width: 400px;
}

.btn-primary:disabled,
.btn-secondary:disabled,
.btn-warning:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-primary:disabled:hover,
.btn-secondary:disabled:hover {
  transform: none;
  box-shadow: none;
  filter: none;
}

/* Error Message */
.error-message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #fee;
  border: 1px solid #fecaca;
  border-radius: $radius-md;
  color: #c33;
  font-size: 0.9rem;
  text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .ccma-webshop-settings {
    padding: 1rem;
  }

  .plan-selection-container {
    padding: 2rem 1.5rem;
  }

  .main-title {
    font-size: 2rem;
  }

  .plans-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .plan-selection-container {
    padding: 1.5rem 1rem;
  }

  .main-title {
    font-size: 1.75rem;
  }

  .trial-info {
    font-size: 1rem;
  }

  .plan-price {
    font-size: 2rem;
  }
}

/* Billing Modal Styles */
.billing-modal-content {
  padding: 1rem 0;
}

.add-card-section h4,
.cards-list-section h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3436;
  margin: 0 0 1.5rem 0;
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(white, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: $radius-md;
}

.processing-overlay p {
  margin-top: 1rem;
  color: #636e72;
  font-weight: 600;
}

.modal-cards-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  max-height: 400px;
  overflow-y: auto;
}

.modal-card-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 2px solid #e0e0e0;
  transition: all 0.2s ease;
}

.modal-card-item:hover {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  background: #f0f7f8;
}

.modal-card-item.is-renewal {
  border-color: #00b894;
  background: #f0fdf9;
}

.modal-card-info {
  flex: 1;
}

.card-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: $spacing-2;
}

.modal-card-number {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3436;
  margin-bottom: 0.25rem;
}

.modal-card-holder {
  font-size: 0.85rem;
  color: #636e72;
  margin-bottom: 0.15rem;
}

.modal-card-expiry {
  font-size: 0.8rem;
  color: #95a5a6;
}

.btn-select-card {
  padding: 0.5rem 1.25rem;
  background: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  border-radius: $radius-md;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-select-card:hover {
  background: #5568d3;
}

.btn-select-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-delete-card {
  padding: 0.5rem 1.25rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: $radius-md;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-delete-card:hover {
  background: #c82333;
}

.btn-delete-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.current-badge {
  padding: 0.5rem 1rem;
  background: #00b894;
  color: white;
  border-radius: $radius-md;
  font-size: 0.85rem;
  font-weight: 600;
}

.btn-add-card {
  width: 100%;
  padding: 0.75rem;
  background: white;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border: 2px dashed var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-radius: $radius-md;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-add-card:hover {
  background: #f5f7ff;
  border-color: #5568d3;
  color: #5568d3;
}

.btn-add-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.add-card-section {
  position: relative;
}

.add-card-section .btn-secondary {
  margin-top: 1rem;
  width: 100%;
}

/* Plan Confirmation Modal Styles */
.confirmation-modal-content {
  padding: 1rem 0;
}

.confirmation-message {
  font-size: 0.95rem;
  color: #636e72;
  margin: 0 0 1.5rem 0;
  text-align: center;
  line-height: 1.5;
}

.selected-plan-summary {
  background: var(--vt-c-white-soft);
  border: 2px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-radius: $radius-lg;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
}

.selected-plan-summary h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3436;
  margin: 0 0 0.75rem 0;
}

.summary-price {
  font-size: 2rem;
  font-weight: 700;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  margin-bottom: 0.5rem;
}

.summary-frequency {
  font-size: 0.9rem;
  color: #636e72;
  margin: 0;
}

.confirmation-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.confirmation-actions .btn-primary,
.confirmation-actions .btn-secondary {
  min-width: 120px;
}
</style>
